'use client'

import type { ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react'
import type { InputHTMLAttributes } from 'react'
import { useTranslations } from 'next-intl'
import { cn } from '@/lib/cn'

/**
 * Champs de formulaire accessibles.
 *
 * Chaque champ : un `<label>` lié, un message d'erreur associé par
 * `aria-describedby`, `aria-invalid` et une cible tactile d'au moins 44 px.
 * Les LIBELLÉS viennent du contenu ; seuls les messages d'erreur sont des
 * libellés techniques traduits (CLAUDE.md § 7).
 */

const CONTROL_BASE =
  'w-full rounded-xl border bg-surface px-4 text-[0.9375rem] text-ink-900 transition-colors ' +
  'placeholder:text-text-subtle focus-visible:outline-3 focus-visible:outline-offset-1 ' +
  'focus-visible:outline-primary-500 disabled:opacity-60'

const CONTROL_HEIGHT = 'h-12'

function controlClass(hasError: boolean, extra?: string) {
  return cn(
    CONTROL_BASE,
    hasError ? 'border-danger' : 'border-border hover:border-border-strong',
    extra,
  )
}

interface FieldShellProps {
  id: string
  label: string
  error?: string
  required?: boolean
  hint?: string
  children: ReactNode
  className?: string
}

/** Enveloppe commune : libellé, champ, indication et message d'erreur. */
export function FieldShell({
  id,
  label,
  error,
  required,
  hint,
  children,
  className,
}: FieldShellProps) {
  const t = useTranslations()

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <label htmlFor={id} className="text-ink-800 text-sm font-medium">
        {label}
        {required ? null : (
          <span className="text-text-subtle ms-1.5 text-xs font-normal">
            ({t('form.optional')})
          </span>
        )}
      </label>

      {children}

      {hint && !error ? (
        <p id={`${id}-hint`} className="text-text-subtle text-xs">
          {hint}
        </p>
      ) : null}

      {error ? (
        <p id={`${id}-error`} className="text-danger text-xs font-medium" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}

interface BaseProps {
  id: string
  label: string
  error?: string
  required?: boolean
  hint?: string
  className?: string
}

export function TextField({
  id,
  label,
  error,
  required,
  hint,
  className,
  ...props
}: BaseProps & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <FieldShell
      id={id}
      label={label}
      error={error}
      required={required}
      hint={hint}
      className={className}
    >
      <input
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        className={controlClass(Boolean(error), CONTROL_HEIGHT)}
        {...props}
      />
    </FieldShell>
  )
}

export function SelectField({
  id,
  label,
  error,
  required,
  hint,
  className,
  children,
  ...props
}: BaseProps & SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <FieldShell
      id={id}
      label={label}
      error={error}
      required={required}
      hint={hint}
      className={className}
    >
      <select
        id={id}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        className={controlClass(Boolean(error), cn(CONTROL_HEIGHT, 'pe-10'))}
        {...props}
      >
        {children}
      </select>
    </FieldShell>
  )
}

export function TextareaField({
  id,
  label,
  error,
  required,
  hint,
  className,
  ...props
}: BaseProps & TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <FieldShell
      id={id}
      label={label}
      error={error}
      required={required}
      hint={hint}
      className={className}
    >
      <textarea
        id={id}
        rows={6}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        className={controlClass(Boolean(error), 'py-3 leading-relaxed')}
        {...props}
      />
    </FieldShell>
  )
}

export function FileField({
  id,
  label,
  error,
  required,
  hint,
  className,
  ...props
}: BaseProps & InputHTMLAttributes<HTMLInputElement>) {
  return (
    <FieldShell
      id={id}
      label={label}
      error={error}
      required={required}
      hint={hint}
      className={className}
    >
      <input
        id={id}
        type="file"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        className={cn(
          'border-border bg-surface text-text-muted w-full rounded-xl border px-4 py-3 text-sm',
          'file:bg-primary-50 file:me-4 file:rounded-lg file:border-0 file:px-4 file:py-2',
          'file:text-primary-700 hover:file:bg-primary-100 file:text-sm file:font-semibold',
          'focus-visible:outline-primary-500 focus-visible:outline-3 focus-visible:outline-offset-1',
          error && 'border-danger',
        )}
        {...props}
      />
    </FieldShell>
  )
}

interface CheckboxFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string
  error?: string
  children: ReactNode
}

export function CheckboxField({ id, error, children, className, ...props }: CheckboxFieldProps) {
  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex items-start gap-3">
        <input
          id={id}
          type="checkbox"
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : undefined}
          className={cn(
            'accent-primary-500 mt-0.5 size-5 shrink-0 cursor-pointer rounded',
            'focus-visible:outline-primary-500 focus-visible:outline-3 focus-visible:outline-offset-2',
            error && 'outline-danger outline',
          )}
          {...props}
        />
        <label htmlFor={id} className="text-text-muted cursor-pointer text-sm leading-relaxed">
          {children}
        </label>
      </div>

      {error ? (
        <p id={`${id}-error`} className="text-danger text-xs font-medium" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}

/** Champ piège anti-robot : masqué visuellement et ignoré des lecteurs d'écran. */
export function HoneypotField(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div aria-hidden="true" className="absolute -start-[9999px] h-0 w-0 overflow-hidden">
      <input type="text" tabIndex={-1} autoComplete="off" {...props} />
    </div>
  )
}
